import { Component } from '@theme/component';

/**
 * Bloco de cupom do carrinho.
 *
 * O código é apenas exibido para o cliente copiar: o checkout é externo (Yampi)
 * e descontos aplicados no carrinho Shopify não atravessam para lá. Por isso
 * aqui não há "aplicar" — só copiar.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [feedback] - Região que anuncia "código copiado".
 * @property {HTMLElement} [code] - Elemento que carrega o código do cupom.
 *
 * @extends {Component<Refs>}
 */
class CartCouponOffer extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
  }

  /**
   * Copia o código do cupom para a área de transferência.
   * @param {MouseEvent} event
   */
  copiar = async (event) => {
    /*
     * `event.target`, não `event.currentTarget`: o tema delega os eventos a
     * partir de um listener no `document` (assets/component.js:223), então
     * `currentTarget` aqui é o próprio document. O `target` é normalizado pelo
     * proxy do tema para o elemento que carrega o atributo `on:click`.
     */
    const botao = event.target;
    if (!(botao instanceof HTMLButtonElement)) return;

    const codigo = this.refs.code?.textContent?.trim();
    if (!codigo) return;

    const rotuloOriginal = botao.dataset.label ?? botao.textContent?.trim() ?? 'Copiar';
    const dicaOriginal = this.refs.hint?.dataset.label ?? this.refs.hint?.textContent?.trim() ?? '';
    botao.dataset.label = rotuloOriginal;
    if (this.refs.hint) this.refs.hint.dataset.label = dicaOriginal;

    const copiado = await this.#copiarTexto(codigo);

    // O aria-label do botão permanece fixo de propósito: trocá-lo com o botão
    // em foco faz o leitor de tela reanunciar o botão inteiro.
    if (this.refs.feedback) {
      this.refs.feedback.textContent = copiado
        ? `Código ${codigo} copiado`
        : `Selecione e copie o código ${codigo}`;
    }

    if (this.refs.hint) {
      this.refs.hint.textContent = copiado
        ? 'Código copiado. Cole no campo de cupom do checkout.'
        : 'Toque e segure no código para copiar.';
    }

    botao.textContent = copiado ? 'Copiado' : rotuloOriginal;
    botao.dataset.copied = String(copiado);

    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      botao.textContent = rotuloOriginal;
      delete botao.dataset.copied;
      if (this.refs.feedback) this.refs.feedback.textContent = '';
    }, 2600);
  };

  /**
   * Cópia em cascata. `navigator.clipboard` falha nos webviews do Instagram e
   * do Facebook no iOS — parcela relevante do tráfego de uma loja de perfumaria.
   * @param {string} texto
   * @returns {Promise<boolean>}
   */
  async #copiarTexto(texto) {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch (_) {
      // Segue para o fallback.
    }

    try {
      const campo = document.createElement('textarea');
      campo.value = texto;
      campo.setAttribute('readonly', '');
      campo.style.position = 'fixed';
      campo.style.opacity = '0';
      document.body.appendChild(campo);
      campo.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(campo);
      if (ok) return true;
    } catch (_) {
      // Segue para o último recurso.
    }

    // Último recurso: seleciona o código na tela para o cliente copiar à mão.
    const alvo = this.refs.code;
    if (alvo) {
      const range = document.createRange();
      range.selectNodeContents(alvo);
      const selecao = window.getSelection();
      selecao?.removeAllRanges();
      selecao?.addRange(range);
    }

    return false;
  }
}

if (!customElements.get('cart-coupon-offer')) {
  customElements.define('cart-coupon-offer', CartCouponOffer);
}
